import { useTranslation } from 'react-i18next';
import { ProviderPaymentRequest } from '@/components/payments/ProviderPaymentRequest';
import { useInvoicePaymentRequests } from '@/hooks/useInvoicePaymentRequests';
import {
  isOpenPaymentRequestStatus,
  isPaymentRequestChannel,
  isPaymentRequestStatus,
  paymentRequestChannelI18nKey,
  paymentRequestStatusI18nKey,
} from '@/lib/paymentRequests';
import { UI_MUTED } from '@/lib/typography';

const PROVIDER_CHANNELS = new Set(['payment_link', 'card', 'mobilepay']);

const SETTLED = new Set(['paid', 'refunded', 'credited', 'void', 'cancelled']);

export function PaymentRequestList({
  invoiceId,
  invoiceStatus,
}: {
  invoiceId: string;
  invoiceStatus?: string | null;
}) {
  const { t } = useTranslation();
  const { data: requests } = useInvoicePaymentRequests(invoiceId);
  if (!requests || requests.length === 0) return null;
  const settled = invoiceStatus != null && SETTLED.has(invoiceStatus);
  const visible = settled
    ? requests.filter((request) => !isOpenPaymentRequestStatus(request.status))
    : requests;
  if (visible.length === 0) return null;

  return (
    <section className="space-y-2" data-payment-requests="">
      <h6 className="text-sm font-medium text-ds-text">
        {t('invoices.payment_requests_title')}
      </h6>
      <ul className="space-y-2">
        {visible.map((request) => {
          const channel = isPaymentRequestChannel(request.channel) ? request.channel : null;
          const status = isPaymentRequestStatus(request.status) ? request.status : null;
          const channelLabel = channel ? t(paymentRequestChannelI18nKey(channel)) : request.channel;
          const statusLabel = status ? t(paymentRequestStatusI18nKey(status)) : request.status;
          const openProvider =
            isOpenPaymentRequestStatus(request.status) && PROVIDER_CHANNELS.has(request.channel);
          return (
            <li
              key={request.id}
              className="rounded-md border border-ds-line p-3"
              data-payment-request={request.channel}
              data-payment-request-status={request.status}
            >
              <p className="text-sm font-medium text-ds-text">
                {channelLabel}
                <span className={`font-normal ${UI_MUTED}`}> · {statusLabel}</span>
              </p>
              {openProvider ? (
                <div className="mt-2">
                  <ProviderPaymentRequest
                    invoiceId={invoiceId}
                    requestId={request.id}
                    payToken={request.pay_token}
                    hideHeading
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
